import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { History } from 'lucide-react';
import { ContinueWatchingTemplateSelector } from './ContinueWatchingTemplateSelector';
import { ContinueWatchingCustomizeTab } from './ContinueWatchingCustomizeTab';
import { VideoFeatures, ContinueWatchingOverlay } from '@/store/videoFeatures';
import { HeroCard } from '@/components/ui/HeroCard';
import { CONTINUE_WATCHING_PREVIEW_EVENT } from '@/components/video-player/hooks/useContinueWatching';

// Import the interface from the index file
import { ContinueWatchingAdvancedConfigProps } from './index';

export const ContinueWatchingAdvancedConfig: React.FC<ContinueWatchingAdvancedConfigProps> = ({
  features,
  setFeature
}) => {
  useLanguage();
  const continueWatchingOverlay = features.continueWatchingOverlay || {
    backgroundColor: '#2563EB',
    textColor: '#FFFFFF',
    buttonColor: '#3b82f6',
    buttonTextColor: '#ffffff',
    title: "Você já começou a assistir esse vídeo",
    continueText: 'Continuar assistindo?',
    restartText: 'Assistir do início',
    template: 'continue',
    useTemplate: true,
    position: 'center',
    autoResume: {
      enabled: false,
      delay: 5,
      showCountdown: true
    }
  };

  const handleTabChange = (value: string) => {
    // Optional: Add any tab change logic here
  };

  // Extract text fields from continueWatchingOverlay for the TextFields component
  const title = features.continueWatchingOverlay?.title || "Você já começou a assistir esse vídeo";
  const continueText = features.continueWatchingOverlay?.continueText || 'Continuar assistindo?';
  const restartText = features.continueWatchingOverlay?.restartText || 'Assistir do início';

  // Template selection props
  const currentTemplate = features.continueWatchingOverlay?.template || 'continue';
  const useTemplate = features.continueWatchingOverlay?.useTemplate !== false; // Default to true if not set

  // Functions to handle template changes
  const handleSelectTemplate = (templateId: string) => {
    setFeature('continueWatchingOverlay', {
      ...(features.continueWatchingOverlay || {}),
      template: templateId,
      useTemplate: true
    });
  };

  // Function to trigger preview of the overlay
  const handleTestOverlay = () => {
    window.dispatchEvent(new CustomEvent(CONTINUE_WATCHING_PREVIEW_EVENT));
  };

  return (
    <div className="flex flex-col h-full">
      {/* Switch Mestre */}
      <div className="px-6 py-4">
        <HeroCard
          icon={<History className="w-full h-full" strokeWidth={2} />}
          title={translateInterfaceText("Continuar de onde parou")}
          description={translateInterfaceText("Exibe modal para retomar reprodução de onde o usuário parou.")}
          isActive={features.continueWatching?.enabled || false}
          onToggle={(enabled) => 
            setFeature('continueWatching', {
              ...(features.continueWatching || {}),
              enabled
            })
          }
        />
      </div>

      {/* Conteúdo (oculto quando desabilitado) */}
      {features.continueWatching?.enabled && (
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          <Tabs defaultValue="templates" onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 h-[44px] bg-white border border-[#E6E6F0] rounded-lg overflow-hidden">
              <TabsTrigger 
                value="templates"
                className="data-[state=active]:bg-[#1F6BFF] data-[state=active]:text-white data-[state=active]:font-semibold rounded-none hover:bg-[#1F6BFF]/10 hover:text-[#1F6BFF] data-[state=active]:hover:bg-[#1F6BFF] data-[state=active]:hover:text-white transition-colors"
              >
                {translateInterfaceText("Templates")}</TabsTrigger>
              <TabsTrigger 
                value="customize"
                className="data-[state=active]:bg-[#1F6BFF] data-[state=active]:text-white data-[state=active]:font-semibold rounded-none hover:bg-[#1F6BFF]/10 hover:text-[#1F6BFF] data-[state=active]:hover:bg-[#1F6BFF] data-[state=active]:hover:text-white transition-colors"
              >
                {translateInterfaceText("Personalização")}</TabsTrigger>
            </TabsList>
            
            <TabsContent value="templates" className="flex-1 mt-0">
              <ContinueWatchingTemplateSelector
                currentTemplate={currentTemplate}
                useTemplate={useTemplate}
                onSelectTemplate={handleSelectTemplate}
                onTestOverlay={handleTestOverlay}
              />
            </TabsContent>
            
            <TabsContent value="customize" className="flex-1 mt-0">
              <ContinueWatchingCustomizeTab 
                overlay={continueWatchingOverlay}
                onOverlayChange={(updates) => {
                  setFeature('continueWatchingOverlay', {
                    ...continueWatchingOverlay,
                    ...updates
                  });
                }}
              />
            </TabsContent>
          </Tabs>
        </div>
      )}
      
    </div>
  );
};
